import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCommentDots, FaEye, FaPlus, FaSearch } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { postCreatecannedmessage } from "../../api/customer.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { hasPermission } from "../../utils/auth";

const TYPES = ["TEXT", "IMAGE", "VIDEO", "DOCUMENT", "AUDIO"];

const emptyForm = () => ({
  name: "",
  type: "TEXT",
  text: "",
  mediaUrl: "",
});

const FetchAllCannedMessage = () => {
  const { projectId } = useProjectPhone();
  const canCreate = hasPermission("CannedMessage_Create");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/fetchcannedmessage/${projectId}`,
      );
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setItems([]);
      setError(err.message || "Failed to load quick replies");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        (item.name || "").toLowerCase().includes(q) ||
        (item.type || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError("");
    setError("");
    setMessage("");
    setModalOpen(true);
  };

  const closeCreate = () => {
    if (saving) return;
    setModalOpen(false);
    setFormError("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!projectId) return;
    const name = form.name.trim();
    if (!name) {
      setFormError("Name is required.");
      return;
    }
    if (form.type !== "TEXT" && !form.mediaUrl.trim()) {
      setFormError("Media URL is required for this type.");
      return;
    }

    setSaving(true);
    setFormError("");
    setError("");
    setMessage("");
    try {
      await postCreatecannedmessage({
        name,
        type: form.type.toLowerCase(),
        text: form.text,
        mediaUrl: form.type === "TEXT" ? "" : form.mediaUrl.trim(),
        fileName: "",
        project_id: projectId,
      });
      setMessage("Canned message saved successfully.");
      setForm(emptyForm());
      setModalOpen(false);
      fetchItems();
    } catch (err) {
      setFormError(err.message || "Failed to create canned message");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-stack">
        <div>
          <h1>
            <FaCommentDots />
            Quick Replies
          </h1>
          <p className="biz-panel-subtitle">
            Quick Replies let you instantly send commonly used messages with a
            single click.
          </p>
        </div>
        {canCreate && (
          <button type="button" className="biz-btn-primary" onClick={openCreate}>
            <FaPlus />
            Create Canned Message
          </button>
        )}
      </div>

      {message && <p className="biz-form-success">{message}</p>}
      {error && <p className="biz-panel-error">{error}</p>}

      <div className="biz-panel-search">
        <FaSearch className="biz-panel-search-icon" />
        <input
          type="search"
          placeholder="Search canned message by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="biz-table-wrap">
        <table className="biz-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Type</th>
              <th className="biz-col-actions">Preview</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="biz-table-empty">
                  Loading quick replies…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="biz-table-empty">
                  No canned messages found.
                </td>
              </tr>
            ) : (
              filtered.map((item, idx) => (
                <tr key={item._id || item.name || idx}>
                  <td>{idx + 1}</td>
                  <td>{item.name || "—"}</td>
                  <td className="biz-status-pill">{item.type || "—"}</td>
                  <td className="biz-col-actions">
                    <div className="biz-table-actions">
                      <button
                        type="button"
                        className="biz-btn-outline biz-btn-sm"
                        onClick={() => setPreview(item)}
                      >
                        <FaEye />
                        Preview
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="biz-modal-backdrop" onClick={closeCreate}>
          <div
            className="biz-modal-card biz-attr-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="create-canned-title"
          >
            <button
              type="button"
              className="biz-modal-close"
              onClick={closeCreate}
              aria-label="Close"
            >
              ×
            </button>
            <h3 id="create-canned-title">Create Canned Message</h3>
            <form onSubmit={onSubmit}>
              <label className="biz-form-field">
                <span>
                  Name <span className="biz-required">*</span>
                </span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter name"
                  required
                  disabled={saving}
                />
              </label>
              <label className="biz-form-field">
                <span>Type</span>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  disabled={saving}
                >
                  {TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
              <label className="biz-form-field">
                <span>Text</span>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
                  rows={4}
                  placeholder="Enter message text"
                  disabled={saving}
                />
              </label>
              {form.type !== "TEXT" && (
                <label className="biz-form-field">
                  <span>
                    Media URL <span className="biz-required">*</span>
                  </span>
                  <input
                    type="url"
                    value={form.mediaUrl}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, mediaUrl: e.target.value }))
                    }
                    placeholder="https://..."
                    disabled={saving}
                  />
                </label>
              )}
              {formError && <p className="biz-form-error">{formError}</p>}
              <div className="biz-modal-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={closeCreate}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="biz-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {preview && (
        <div className="biz-modal-backdrop" onClick={() => setPreview(null)}>
          <div
            className="biz-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="canned-preview-title"
          >
            <h3 id="canned-preview-title">Message Preview</h3>
            <p>
              <strong>Name:</strong> {preview.name}
            </p>
            <p>
              <strong>Type:</strong> {preview.type}
            </p>
            {preview.text && (
              <div className="biz-canned-preview-text">
                <strong>Text:</strong>
                <p>{preview.text}</p>
              </div>
            )}
            {preview.mediaUrl && (
              <p>
                <strong>Media:</strong>{" "}
                <a href={preview.mediaUrl} target="_blank" rel="noreferrer">
                  {preview.mediaUrl}
                </a>
              </p>
            )}
            <button
              type="button"
              className="biz-btn-primary"
              onClick={() => setPreview(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FetchAllCannedMessage;
